import React from 'react';
import { Eye, EyeOff, HandHeart, LucideIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from 'src/shared/components/ui/tooltip';
import ReleasedAutomaticallyIcon from 'src/shared/components/released-automatically-icon/ReleasedAutomaticallyIcon';
import { ProductVisibility, ReleasePolicy } from 'src/shared/types/engine/products';
import { defaultBehaviour } from './defaultBehaviour';

/** `null` is the default: the product has no setting. */
export type Choice = ProductVisibility | null;

type ChoiceIcon = LucideIcon | ((props: { size: number; 'aria-hidden': boolean }) => React.ReactElement);

type ChoiceOption = { choice: Choice; label: string; tooltip: React.ReactNode; Icon: ChoiceIcon };

/**
 * Ordered from least to most the customer sees, with the default in the middle.
 *
 * An automatic product has no Visible: it releases its own results, and the
 * engine refuses that setting for it. An automatic product with no upsell
 * page calls its default Released, because the customer sees no upsell.
 */
const choicesFor = (releasePolicy: ReleasePolicy, hasUpsellPage: boolean, isTurnedOnPerProject: boolean): ChoiceOption[] => {
    const isAutomatic = releasePolicy === ReleasePolicy.AUTOMATIC;
    const hidden: ChoiceOption = {
        choice: ProductVisibility.HIDDEN,
        label: 'Hidden',
        tooltip: (
            <>
                <span className='font-medium'>Hidden.</span>{' '}
                {isAutomatic
                    ? 'The customer sees nothing of this product, not even released results.'
                    : 'The customer sees nothing of this product.'}
            </>
        ),
        Icon: EyeOff,
    };
    const upsell: ChoiceOption = {
        choice: null,
        label: 'Upsell',
        tooltip: `(Default) ${defaultBehaviour(releasePolicy, hasUpsellPage, isTurnedOnPerProject)}`,
        Icon: isAutomatic ? ReleasedAutomaticallyIcon : HandHeart,
    };
    const released: ChoiceOption = {
        choice: null,
        label: 'Released',
        tooltip: `(Default) ${defaultBehaviour(releasePolicy, hasUpsellPage, isTurnedOnPerProject)}`,
        Icon: ReleasedAutomaticallyIcon,
    };
    const visible: ChoiceOption = {
        choice: ProductVisibility.VISIBLE,
        label: 'Visible',
        tooltip: (
            <>
                <span className='font-medium'>Visible.</span> The customer sees the results.
            </>
        ),
        Icon: Eye,
    };

    if (isAutomatic && hasUpsellPage) {
        return [hidden, upsell];
    } else if (isAutomatic) {
        return [hidden, released];
    } else {
        return [hidden, upsell, visible];
    }
};

/**
 * A setting fills with the brand colour; the default stays neutral, so the
 * control shows at a glance whether a product has a setting other than the default.
 */
const chosenStyle = (choice: Choice): string =>
    choice ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-white text-grey-100 shadow-sm ring-1 ring-grey-40';

type VisibilityToggleProps = {
    label: string;
    releasePolicy: ReleasePolicy;
    hasUpsellPage: boolean;
    isTurnedOnPerProject: boolean;
    setting: ProductVisibility | undefined;
    disabled: boolean;
    onChoose: (choice: Choice) => void;
};

/**
 * One of three, as icons of a fixed size that never leave the screen, so the
 * row does not move when the choice changes. The name of each option is in
 * its tooltip and, for screen readers, in hidden text.
 *
 * Upsell is an option here but not a value the engine stores: choosing it
 * removes the setting. Native radios give arrow-key movement and the
 * radiogroup semantics, and a radio that is already checked sends no change,
 * so choosing the current option sends no request.
 */
const VisibilityToggle = ({
    label,
    releasePolicy,
    hasUpsellPage,
    isTurnedOnPerProject,
    setting,
    disabled,
    onChoose,
}: VisibilityToggleProps) => {
    const current: Choice = setting ?? null;

    return (
        <TooltipProvider delayDuration={200}>
            <div
                role='radiogroup'
                aria-label={label}
                aria-disabled={disabled}
                className='inline-flex gap-0.5 rounded-md border border-grey-20 bg-grey-0 p-0.5 aria-disabled:opacity-50'
            >
                {choicesFor(releasePolicy, hasUpsellPage, isTurnedOnPerProject).map(({ choice, label: optionLabel, tooltip, Icon }) => {
                    const isChosen = current === choice;

                    return (
                        <label
                            key={optionLabel}
                            className={`relative flex size-7 cursor-pointer items-center justify-center rounded-sm transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary has-[:disabled]:cursor-not-allowed ${
                                isChosen ? chosenStyle(choice) : 'text-grey-70 hover:text-black'
                            }`}
                        >
                            <input
                                type='radio'
                                name={`visibility-${label}`}
                                className='sr-only'
                                checked={isChosen}
                                disabled={disabled}
                                onChange={() => onChoose(choice)}
                            />
                            <span className='sr-only'>{optionLabel}</span>
                            {/* The trigger is the icon, not the label: the sheet focuses
                                the first radio when it opens, and a trigger round the radio
                                would open its tooltip then, unasked. */}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span
                                        className='absolute inset-0 flex items-center justify-center'
                                        data-testid={`visibility-option-${optionLabel}`}
                                    >
                                        <Icon size={14} aria-hidden />
                                    </span>
                                </TooltipTrigger>
                                <TooltipPortal>
                                    <TooltipContent side='left'>{tooltip}</TooltipContent>
                                </TooltipPortal>
                            </Tooltip>
                        </label>
                    );
                })}
            </div>
        </TooltipProvider>
    );
};

export default VisibilityToggle;
